import React, { useState, useEffect, useMemo, useCallback } from 'react';

const API_URL = process.env.REACT_APP_API_URL || '/api';

const CLUSTERS = {
    internal:  { label: 'Internal Team',      color: '#8b5cf6', pos: [-300,    0] },
    deals:     { label: 'Deals',              color: '#2563eb', pos: [   0, -250] },
    external:  { label: 'External Contacts',  color: '#10b981', pos: [ 300,    0] },
    investors: { label: 'Investors',          color: '#f59e0b', pos: [   0,  250] },
};

const parseNames = (s) => {
    if (!s) return [];
    if (Array.isArray(s)) return s.map(x => String(x).trim()).filter(Boolean);
    const str = String(s).trim();
    if (str.startsWith('[') || str.startsWith('{')) {
        try { const p = JSON.parse(str); if (Array.isArray(p)) return p.map(x => String(x).trim()).filter(Boolean); } catch { }
    }
    return str.split(/[,;]/).map(x => x.trim().replace(/^["']|["']$/g, '')).filter(Boolean);
};

const MAX_FAN = 20;

export default function NetworkGraphNew() {
    const [deals, setDeals] = useState([]);
    const [internal, setInternal] = useState([]);
    const [externals, setExternals] = useState([]);
    const [investors, setInvestors] = useState([]);
    const [loading, setLoading] = useState(true);

    const [expanded, setExpanded] = useState(null);     // 'internal' | 'deals' | 'external' | 'partners' | null
    const [selected, setSelected] = useState(null);     // { cluster, id, name, data }
    const [overflowCluster, setOverflowCluster] = useState(null); // cluster whose '+N more' is open

    useEffect(() => {
        (async () => {
            try {
                const [d, i, e, p] = await Promise.all([
                    fetch(`${API_URL}/deals`).then(r => r.json()).catch(() => []),
                    fetch(`${API_URL}/internal`).then(r => r.json()).catch(() => []),
                    fetch(`${API_URL}/externalcontacts`).then(r => r.json()).catch(() => []),
                    fetch(`${API_URL}/investors`).then(r => r.json()).catch(() => []),
                ]);
                setDeals(Array.isArray(d) ? d : []);
                setInternal(Array.isArray(i) ? i : []);
                setExternals(Array.isArray(e) ? e : []);
                setInvestors(Array.isArray(p) ? p : []);
            } finally { setLoading(false); }
        })();
    }, []);

    const itemsFor = useCallback((cluster) => {
        if (cluster === 'internal') return internal;
        if (cluster === 'external') return externals;
        if (cluster === 'deals') return deals;
        if (cluster === 'investors') return investors;
        return [];
    }, [internal, externals, deals, investors]);

    const itemName = (item, cluster) => {
        if (cluster === 'internal' || cluster === 'external') return item.Name;
        if (cluster === 'deals') return item.Title || item.Company || 'Untitled';
        if (cluster === 'investors') return item.Name || 'Investor';
        return '';
    };

    // Connections per rules (with fuzzy fallbacks)
    const getConnections = useCallback((item, cluster) => {
        const out = { internal: [], deals: [], external: [], investors: [] };
        if (!item) return out;
        const push = (arr, x) => { if (x && !arr.find(a => a.Id === x.Id)) arr.push(x); };

        if (cluster === 'internal') {
            const name = (item.Name || '').toLowerCase();
            // A) match via ContexQContacts name
            deals.filter(d => parseNames(d.ContexQContacts).some(n => n.toLowerCase() === name))
                .forEach(d => push(out.deals, d));
            // B) fallback: Internal.DealName matches Deal.Title
            if (item.DealName) {
                deals.filter(d => (d.Title || '').toLowerCase() === item.DealName.toLowerCase())
                    .forEach(d => push(out.deals, d));
            }
            // external contacts on those deals: via CustomerContacts OR via matching company
            out.deals.forEach(d => {
                parseNames(d.CustomerContacts).forEach(n => push(out.external, externals.find(e => (e.Name || '').toLowerCase() === n.toLowerCase())));
                externals.filter(e => (e.Company || '').toLowerCase() === (d.Company || '').toLowerCase()).forEach(e => push(out.external, e));
            });
        } else if (cluster === 'external') {
            const name = (item.Name || '').toLowerCase();
            const company = (item.Company || '').toLowerCase();
            // A) deals where name in CustomerContacts
            deals.filter(d => parseNames(d.CustomerContacts).some(n => n.toLowerCase() === name))
                .forEach(d => push(out.deals, d));
            // B) fallback: deals at same company
            if (company) {
                deals.filter(d => (d.Company || '').toLowerCase() === company).forEach(d => push(out.deals, d));
            }
            // internal on those deals: via ContexQContacts OR via DealName match
            out.deals.forEach(d => {
                parseNames(d.ContexQContacts).forEach(n => push(out.internal, internal.find(i => (i.Name || '').toLowerCase() === n.toLowerCase())));
                internal.filter(i => (i.DealName || '').toLowerCase() === (d.Title || '').toLowerCase()).forEach(i => push(out.internal, i));
            });
        } else if (cluster === 'deals') {
            parseNames(item.ContexQContacts).forEach(n => push(out.internal, internal.find(i => (i.Name || '').toLowerCase() === n.toLowerCase())));
            // fallback: internals with matching DealName
            internal.filter(i => (i.DealName || '').toLowerCase() === (item.Title || '').toLowerCase()).forEach(i => push(out.internal, i));
            parseNames(item.CustomerContacts).forEach(n => push(out.external, externals.find(e => (e.Name || '').toLowerCase() === n.toLowerCase())));
            // fallback: externals with matching Company
            externals.filter(e => (e.Company || '').toLowerCase() === (item.Company || '').toLowerCase()).forEach(e => push(out.external, e));
        } else if (cluster === 'investors') {
            // investor.Contact may match internal or external by name
            if (item.Contact) {
                const c = item.Contact.toLowerCase();
                const i = internal.find(x => (x.Name || '').toLowerCase() === c);
                if (i) push(out.internal, i);
                const e = externals.find(x => (x.Name || '').toLowerCase() === c);
                if (e) push(out.external, e);
            }
        }
        return out;
    }, [deals, internal, externals]);

    const selectedConnections = useMemo(
        () => selected ? getConnections(selected.data, selected.cluster) : null,
        [selected, getConnections]
    );

    // Expanded cluster fan positions (up to MAX_FAN)
    const expandedPositions = useMemo(() => {
        if (!expanded) return { positions: {}, overflow: [] };
        const items = itemsFor(expanded);
        if (!items.length) return { positions: {}, overflow: [] };
        const positions = {};
        const center = CLUSTERS[expanded].pos;
        // Fan direction: away from ContexQ (0,0)
        const outwardAngle = Math.atan2(center[1], center[0]);
        const fanSpan = Math.min(Math.PI * 0.9, Math.PI * 0.35 + items.length * 0.06);
        // Per-cluster max radius — larger clusters (left/right) get more room
        const MAX_R = { internal: 200, external: 200, deals: 70, partners: 70 };
        const radius = Math.max(60, Math.min(MAX_R[expanded] || 140, 55 + items.length * 8));
        const show = items.slice(0, MAX_FAN);
        const overflow = items.slice(MAX_FAN);
        show.forEach((item, i) => {
            const ratio = show.length === 1 ? 0.5 : i / (show.length - 1);
            const angle = outwardAngle - fanSpan / 2 + ratio * fanSpan;
            positions[`${expanded}-${item.Id}`] = [center[0] + Math.cos(angle) * radius, center[1] + Math.sin(angle) * radius];
        });
        if (overflow.length > 0) {
            // Put +N node at the end of arc
            const endRatio = 1;
            const angle = outwardAngle - fanSpan / 2 + endRatio * fanSpan + 0.15;
            positions[`${expanded}-__overflow__`] = [center[0] + Math.cos(angle) * (radius + 10), center[1] + Math.sin(angle) * (radius + 10)];
        }
        return { positions, overflow };
    }, [expanded, itemsFor]);

    // Background click / Escape reset
    const reset = () => { setExpanded(null); setSelected(null); setOverflowCluster(null); };

    useEffect(() => {
        const onKey = (e) => { if (e.key === 'Escape') reset(); };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, []);

    // Deal node size by value
    const dealRadius = (d) => {
        const v = Number(d?.Value) || 0;
        return Math.max(10, Math.min(22, 10 + Math.sqrt(v) / 100));
    };

    const nodeRadius = (item, cluster) => {
        if (cluster === 'deals') return dealRadius(item);
        return 12;
    };

    // Is a given node highlighted by current selection?
    const isHighlighted = (cluster, id) => {
        if (!selected || !selectedConnections) return false;
        if (selected.cluster === cluster && selected.id === id) return true;
        return (selectedConnections[cluster] || []).some(x => x.Id === id);
    };

    if (loading) return <div style={{ padding: 40, color: '#6b7280' }}>Loading network…</div>;

    const selectedKey = selected ? `${selected.cluster}-${selected.id}` : null;
    const selectedPos = selectedKey ? expandedPositions.positions[selectedKey] : null;

    return (
        <div onClick={reset} style={{ position: 'relative', width: '100%', height: 'calc(100vh - 160px)', background: '#f8fafc', borderRadius: 12, overflow: 'hidden' }}>
            {/* Header */}
            <div style={{ position: 'absolute', top: 20, left: 20, zIndex: 10, color: '#0f172a', pointerEvents: 'none' }}>
                <div style={{ fontSize: 11, color: '#64748b', textTransform: 'uppercase', letterSpacing: 1, fontWeight: 700 }}>Network Graph</div>
                <div style={{ fontSize: 18, fontWeight: 800, marginTop: 2 }}>
                    {expanded ? CLUSTERS[expanded].label : 'ContexQ Relationship Map'}
                </div>
                <div style={{ fontSize: 11, color: '#64748b', marginTop: 4 }}>
                    {expanded ? 'Click a node to see its connections · Escape to collapse' : 'Click a cluster to expand'}
                </div>
            </div>

            {/* Legend */}
            <div onClick={e => e.stopPropagation()} style={{ position: 'absolute', top: 20, right: 20, zIndex: 10, background: '#fff', border: '1px solid #e2e8f0', borderRadius: 10, padding: 10, fontSize: 11, boxShadow: '0 1px 2px rgba(0,0,0,0.04)' }}>
                {Object.entries(CLUSTERS).map(([k, c]) => (
                    <div key={k} style={{ display: 'flex', alignItems: 'center', gap: 8, color: '#334155', marginBottom: 3 }}>
                        <span style={{ width: 10, height: 10, borderRadius: '50%', background: c.color }} />
                        <span>{c.label}</span>
                    </div>
                ))}
            </div>

            {/* SVG graph */}
            <svg viewBox="-500 -400 1000 800" preserveAspectRatio="xMidYMid meet" style={{ width: '100%', height: '100%' }}
                onClick={e => e.stopPropagation()}>
                <defs>
                    <filter id="ngShadow"><feDropShadow dx="0" dy="2" stdDeviation="3" floodOpacity="0.15" /></filter>
                </defs>

                {/* Lines from ContexQ center to each cluster */}
                {Object.entries(CLUSTERS).map(([k, c]) => (
                    <line key={k} x1={0} y1={0} x2={c.pos[0]} y2={c.pos[1]}
                        stroke={c.color} strokeWidth={1.5}
                        opacity={expanded === k ? 0.55 : (expanded ? 0.15 : 0.3)} />
                ))}

                {/* Expanded cluster: lines from cluster center to each visible member */}
                {expanded && itemsFor(expanded).slice(0, MAX_FAN).map(item => {
                    const key = `${expanded}-${item.Id}`;
                    const pos = expandedPositions.positions[key];
                    if (!pos) return null;
                    const highlighted = isHighlighted(expanded, item.Id);
                    const dim = selected && !highlighted;
                    return (
                        <line key={key} x1={CLUSTERS[expanded].pos[0]} y1={CLUSTERS[expanded].pos[1]} x2={pos[0]} y2={pos[1]}
                            stroke={CLUSTERS[expanded].color} strokeWidth={1}
                            opacity={dim ? 0.05 : 0.4} />
                    );
                })}

                {/* Highlight edges (routed through ContexQ center) */}
                {selected && selectedConnections && selectedPos && (
                    <>
                        {Object.keys(CLUSTERS).map(cluster => {
                            const conns = selectedConnections[cluster] || [];
                            return conns.map(conn => {
                                const targetCenter = CLUSTERS[cluster].pos;
                                // If target is in currently expanded cluster AND we have its exact position, end at it; else end at cluster center
                                const targetPos = expanded === cluster ? (expandedPositions.positions[`${cluster}-${conn.Id}`] || targetCenter) : targetCenter;
                                return (
                                    <polyline key={`hl-${cluster}-${conn.Id}`}
                                        points={`${selectedPos[0]},${selectedPos[1]} 0,0 ${targetPos[0]},${targetPos[1]}`}
                                        fill="none" stroke="#f5c451" strokeWidth={2} opacity={0.85} strokeDasharray="6 4" />
                                );
                            });
                        })}
                    </>
                )}

                {/* Cluster nodes (always visible) */}
                {Object.entries(CLUSTERS).map(([k, c]) => {
                    const count = itemsFor(k).length;
                    const isExpanded = expanded === k;
                    const isDimmed = expanded && !isExpanded;
                    return (
                        <g key={k} transform={`translate(${c.pos[0]},${c.pos[1]})`}
                            style={{ cursor: 'pointer' }}
                            opacity={isDimmed ? 0.35 : 1}
                            onClick={(e) => {
                                e.stopPropagation();
                                setExpanded(isExpanded ? null : k);
                                setSelected(null);
                                setOverflowCluster(null);
                            }}>
                            <circle r={isExpanded ? 34 : 28} fill={c.color} stroke="#fff" strokeWidth={2} filter="url(#ngShadow)" />
                            <text textAnchor="middle" dy={5} style={{ fontSize: 14, fill: '#fff', fontWeight: 700, pointerEvents: 'none' }}>{count}</text>
                            <text textAnchor="middle" y={isExpanded ? 54 : 48} style={{ fontSize: 11, fill: '#0f172a', fontWeight: 700, pointerEvents: 'none' }}>{c.label}</text>
                        </g>
                    );
                })}

                {/* Expanded members */}
                {expanded && itemsFor(expanded).slice(0, MAX_FAN).map(item => {
                    const key = `${expanded}-${item.Id}`;
                    const pos = expandedPositions.positions[key];
                    if (!pos) return null;
                    const isSel = selected && selected.cluster === expanded && selected.id === item.Id;
                    const highlighted = isHighlighted(expanded, item.Id);
                    const dim = selected && !highlighted;
                    const r = nodeRadius(item, expanded);
                    const name = itemName(item, expanded);
                    // Label on the side away from the cluster's primary axis, safely inside viewBox
                    let labelX = 0, labelY = 0, anchor = 'middle';
                    if (expanded === 'deals') { labelX = 0; labelY = -(r + 6); anchor = 'middle'; }
                    else if (expanded === 'partners') { labelX = 0; labelY = r + 14; anchor = 'middle'; }
                    else if (expanded === 'internal') { labelX = -(r + 6); labelY = 4; anchor = 'end'; }
                    else if (expanded === 'external') { labelX = r + 6; labelY = 4; anchor = 'start'; }
                    return (
                        <g key={key} transform={`translate(${pos[0]},${pos[1]})`} style={{ cursor: 'pointer' }}
                            opacity={dim ? 0.15 : 1}
                            onClick={(e) => { e.stopPropagation(); setSelected({ cluster: expanded, id: item.Id, name, data: item }); }}>
                            {isSel && <circle r={r + 6} fill="none" stroke={CLUSTERS[expanded].color} strokeWidth={2.5} />}
                            <circle r={r} fill={CLUSTERS[expanded].color} stroke="#fff" strokeWidth={1.5} filter="url(#ngShadow)" />
                            <text textAnchor={anchor} x={labelX} y={labelY} style={{ fontSize: 10, fill: '#0f172a', fontWeight: isSel ? 700 : 500, pointerEvents: 'none' }}>
                                {(name || '').length > 20 ? (name || '').slice(0, 19) + '…' : name}
                            </text>
                        </g>
                    );
                })}

                {/* "+N more" node if overflow */}
                {expanded && expandedPositions.overflow.length > 0 && (() => {
                    const pos = expandedPositions.positions[`${expanded}-__overflow__`];
                    if (!pos) return null;
                    return (
                        <g transform={`translate(${pos[0]},${pos[1]})`} style={{ cursor: 'pointer' }}
                            onClick={(e) => { e.stopPropagation(); setOverflowCluster(expanded); }}>
                            <circle r={14} fill="#64748b" stroke="#fff" strokeWidth={1.5} filter="url(#ngShadow)" />
                            <text textAnchor="middle" dy={4} style={{ fontSize: 10, fill: '#fff', fontWeight: 700, pointerEvents: 'none' }}>
                                +{expandedPositions.overflow.length}
                            </text>
                            <text textAnchor="middle" y={28} style={{ fontSize: 9, fill: '#475569', fontWeight: 600, pointerEvents: 'none' }}>more</text>
                        </g>
                    );
                })()}

                {/* ContexQ center — always on top, never moves */}
                <g>
                    <circle cx={0} cy={0} r={42} fill="#1e293b" stroke="#fff" strokeWidth={3} filter="url(#ngShadow)" />
                    <text textAnchor="middle" dy={5} style={{ fontSize: 13, fill: '#fff', fontWeight: 800, letterSpacing: 0.5, pointerEvents: 'none' }}>ContexQ</text>
                </g>
            </svg>

            {/* Detail panel — shown when a node is selected */}
            {selected && (
                <div onClick={e => e.stopPropagation()}
                    style={{ position: 'absolute', top: 20, right: 20, width: 300, maxHeight: 'calc(100% - 40px)', background: '#fff', borderRadius: 12, boxShadow: '0 10px 30px rgba(0,0,0,0.12)', border: '1px solid #e2e8f0', overflow: 'hidden', zIndex: 11 }}>
                    <div style={{ padding: '14px 16px', borderBottom: '1px solid #f1f5f9', display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: CLUSTERS[selected.cluster].color, color: '#fff' }}>
                        <div>
                            <div style={{ fontSize: 10, textTransform: 'uppercase', letterSpacing: 1, opacity: 0.9, fontWeight: 700 }}>{selected.cluster}</div>
                            <div style={{ fontSize: 15, fontWeight: 800 }}>{selected.name}</div>
                        </div>
                        <button onClick={() => setSelected(null)} style={{ background: 'none', border: 'none', color: '#fff', cursor: 'pointer', fontSize: 18 }}>×</button>
                    </div>
                    <div style={{ padding: 14, overflowY: 'auto', maxHeight: 420 }}>
                        <DetailFields cluster={selected.cluster} data={selected.data} />
                        {selectedConnections && (
                            <div style={{ marginTop: 14, borderTop: '1px solid #f1f5f9', paddingTop: 12 }}>
                                <div style={{ fontSize: 10, color: '#64748b', textTransform: 'uppercase', letterSpacing: 1, fontWeight: 700, marginBottom: 6 }}>Connected</div>
                                {Object.entries(selectedConnections).map(([cluster, arr]) => arr.length > 0 && (
                                    <div key={cluster} style={{ marginBottom: 8 }}>
                                        <div style={{ fontSize: 11, color: CLUSTERS[cluster].color, fontWeight: 700, marginBottom: 3 }}>{CLUSTERS[cluster].label} ({arr.length})</div>
                                        {arr.map(x => (
                                            <div key={x.Id} style={{ fontSize: 12, color: '#334155', padding: '3px 0' }}>
                                                • {x.Name || x.Title || x.Company || x.Partner}
                                            </div>
                                        ))}
                                    </div>
                                ))}
                                {Object.values(selectedConnections).every(a => a.length === 0) && (
                                    <div style={{ fontSize: 11, color: '#94a3b8' }}>No connections found in CRM data.</div>
                                )}
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* Overflow side list ("+N more") */}
            {overflowCluster && (
                <div onClick={e => e.stopPropagation()}
                    style={{ position: 'absolute', top: 0, right: 0, height: '100%', width: 300, background: '#fff', borderLeft: '1px solid #e2e8f0', boxShadow: '-8px 0 20px rgba(0,0,0,0.08)', zIndex: 12, display: 'flex', flexDirection: 'column' }}>
                    <div style={{ padding: '12px 14px', borderBottom: '1px solid #f1f5f9', display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: CLUSTERS[overflowCluster].color, color: '#fff' }}>
                        <div style={{ fontWeight: 700, fontSize: 14 }}>All {CLUSTERS[overflowCluster].label} ({itemsFor(overflowCluster).length})</div>
                        <button onClick={() => setOverflowCluster(null)} style={{ background: 'none', border: 'none', color: '#fff', cursor: 'pointer', fontSize: 18 }}>×</button>
                    </div>
                    <div style={{ flex: 1, overflowY: 'auto', padding: 8 }}>
                        {itemsFor(overflowCluster).map(item => {
                            const name = itemName(item, overflowCluster);
                            return (
                                <div key={item.Id}
                                    onClick={() => {
                                        setSelected({ cluster: overflowCluster, id: item.Id, name, data: item });
                                        setOverflowCluster(null);
                                    }}
                                    style={{ padding: '8px 10px', fontSize: 13, color: '#0f172a', cursor: 'pointer', borderRadius: 6 }}
                                    onMouseEnter={e => e.currentTarget.style.background = '#f1f5f9'}
                                    onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                                    {name}
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}
        </div>
    );
}

function DetailFields({ cluster, data }) {
    const rows = [];
    if (cluster === 'internal') {
        rows.push(['Email', data.Email]); rows.push(['Territory', data.Territory]); rows.push(['Deal', data.DealName]);
    } else if (cluster === 'external') {
        rows.push(['Company', data.Company]); rows.push(['Position', data.Position]); rows.push(['Type', data.ContactType]);
    } else if (cluster === 'deals') {
        rows.push(['Company', data.Company]);
        rows.push(['Value', data.Value ? `$${Number(data.Value).toLocaleString()}` : null]);
        rows.push(['Stage', data.Stage]);
        rows.push(['Probability', data.Probability ? `${data.Probability}%` : null]);
    } else if (cluster === 'investors') {
        rows.push(['Type', data.Type]); rows.push(['Amount', data.Amount ? `$${data.Amount}` : null]);
        rows.push(['Stage', data.Stage]); rows.push(['Status', data.Status]); rows.push(['Contact', data.Contact]);
    }
    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {rows.filter(([, v]) => v).map(([label, v]) => (
                <div key={label}>
                    <div style={{ fontSize: 10, color: '#64748b', textTransform: 'uppercase', letterSpacing: 0.8, fontWeight: 600 }}>{label}</div>
                    <div style={{ fontSize: 13, color: '#0f172a' }}>{v}</div>
                </div>
            ))}
        </div>
    );
}
